import {Component, OnInit, ViewChild, ChangeDetectionStrategy} from '@angular/core';
import {SessionService} from '../../../core/services/session.service';
import {User, UserPatch} from '../../../shared/models/user';
import {TitleChangedAction} from '../../../core/actions/title.actions';
import {Store} from '@ngrx/store';
import {AppState} from '../../../app/app.state';
import {UsersService} from '../../../core/services/users.service';
import {ShadeToolsService} from '../../../core/services/shade-tools.service';
import {SaveRevertButtonsComponent, SaveRevertButtonsStatus} from '../../components/save-revert-buttons/save-revert-buttons.component';
import {AsideService} from '../../../core/services/aside.service';
import {HttpErrorResponse} from '@angular/common/http';
import {UserSettings} from '../../../shared/models/user-settings';
import {FormsModule} from '@angular/forms';
import {InfoCircleComponent} from '../../components/info-circle/info-circle.component';

@Component({
  selector: 'motn-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FormsModule,
    SaveRevertButtonsComponent,
    InfoCircleComponent,
  ],
})
export class UserProfileComponent implements OnInit {
  user: User;
  userSettings: UserSettings = new UserSettings();

  @ViewChild('saveRevertButtonsComponent') buttons: SaveRevertButtonsComponent;
  status: SaveRevertButtonsStatus;


  constructor(
    private store: Store<AppState>,
    private session: SessionService,
    private shadeTools: ShadeToolsService,
    private asideService: AsideService,
    private usersService: UsersService,
  ) {
  }


  ngOnInit(): void {
    this.revert();
    this.updateTitle();
    this.updateAside();
    this.pullUserSettings();
  }


  pullUserSettings() {
    this.usersService.getUserSettings$(this.user.ID)
      .subscribe(userSettings => this.userSettings.update(userSettings));
  }


  revert(): void {
    this.status = '';
    this.user = JSON.parse(JSON.stringify(this.session.user));
  }


  submit(): void {
    if (!this.user.Name.trim()) {
      this.shadeTools.errorDialog('A name must be filled in.');
      this.buttons.resetStatus();
      return;
    }

    if (!this.user.Email) {
      this.shadeTools.errorDialog('An email address must be filled in.');
      this.buttons.resetStatus();
      return;
    }

    this.status = 'submitting';

    const patch: UserPatch = {};

    if (this.user.Name !== this.session.user.Name) {
      patch.Name = this.user.Name;
    }

    if (this.user.Email !== this.session.user.Email) {
      patch.Email = this.user.Email;
    }

    this.usersService.patchUser$(patch, this.user.ID).subscribe(
      () => {
        this.buttons.showSavedStatus();
        this.session.user = this.user;
      },
      (error: HttpErrorResponse) => {
        if (error.message.includes('mail')) {
          this.shadeTools.errorDialog('The email address provided is not formatted correctly.');
        } else {
          this.shadeTools.errorDialog();
        }

        this.buttons.resetStatus();
      },
    );
  }


  updateStatus(status: SaveRevertButtonsStatus) {
    this.status = status;
  }


  private updateTitle() {
    this.store.dispatch(new TitleChangedAction(
      '',
      'My User Profile',
      [{text: this.user.Username}],
    ));
  }


  private updateAside() {
    this.asideService.replaceContextSections([]);
  }


  get emailNotifications(): boolean {
    return this.userSettings?.Alarms?.Targets?.Email.Enabled;
  }


  set emailNotifications(value: boolean) {
    this.userSettings.Alarms.Targets.Email.Enabled = value;
    this.usersService.saveUserSettings$(this.user.ID, this.userSettings).subscribe();
  }


  get pushNotifications(): boolean {
    return this.userSettings?.Alarms?.Targets?.Push.Enabled;
  }


  set pushNotifications(value: boolean) {
    this.userSettings.Alarms.Targets.Push.Enabled = value;
    this.usersService.saveUserSettings$(this.user.ID, this.userSettings).subscribe();
  }
}
